import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LoadingButton,
  Row,
  Typography,
} from '@tempmee/react';
import { OutlinePlusCircleIcon } from '@tempmee/react-icons';
import { useSetSalesMetroAdmins } from '../../../../domain/SalesMetros';
import { SelectFilter } from '../../../commonComponents/filters/SelectFilter';
import { UNKNOWN_ADMIN_LABEL, salesMetroRowPropType } from '../constants';

const SAVE_FAILED_MESSAGE = 'Could not save the assigned admins. Please try again.';

const chipLabel = (namesById, internalUserId) =>
  namesById.get(internalUserId) ?? `${UNKNOWN_ADMIN_LABEL} (${internalUserId})`;

const isSameSet = (left, right) =>
  left.length === right.length && left.every((id) => right.includes(id));

/**
 * Edits which GoTu admins one metro is assigned to (GOTU-6009). Any admin can
 * be added, whatever their role; Save sends the whole selected set in one PUT.
 *
 * Mounted only while open, so every opening starts from the metro's saved set
 * and no leftover error.
 *
 * Every admin sees Edit — product's call on GOTU-6009. The claim that allows
 * saving lives only in the token, and organization-service answers an admin
 * without it with a 403 whose message is written for them. That message, like
 * any other refusal's, is shown verbatim and inline, and the dialog stays open.
 *
 * An assigned id missing from the admin list stays a chip, so a Save cannot
 * silently unassign someone the service's mirror has not caught up with.
 */
export const EditMetroAdminsDialog = ({ metro, admins, onClose }) => {
  const [selectedIds, setSelectedIds] = useState(metro.assignedInternalUserIds);
  const [submitError, setSubmitError] = useState(null);

  const saveAdmins = useSetSalesMetroAdmins(metro.id);

  const namesById = useMemo(
    () => new Map(admins.map((admin) => [admin.internalUserId, admin.name])),
    [admins],
  );

  const addOptions = admins
    .filter((admin) => !selectedIds.includes(admin.internalUserId))
    .map((admin) => ({ value: admin.internalUserId, label: admin.name }));

  const handleAdd = (internalUserId) => setSelectedIds((ids) => [...ids, internalUserId]);

  const handleRemove = (internalUserId) =>
    setSelectedIds((ids) => ids.filter((id) => id !== internalUserId));

  const handleSave = () => {
    setSubmitError(null);
    // Per-call callbacks, so a request that settles after the dialog was
    // closed does not close (or error) whatever is open by then.
    saveAdmins.mutate(selectedIds, {
      onSuccess: onClose,
      onError: (error) => setSubmitError(error?.response?.data?.error || SAVE_FAILED_MESSAGE),
    });
  };

  return (
    <Dialog open onClose={onClose} closeOnOverlayClick={false}>
      <DialogTitle>Edit Admins</DialogTitle>

      <DialogContent>
        <Column gap={4}>
          {/* Nothing is ever shown as selected: picking an admin adds a chip
              below and drops them from these options. */}
          <SelectFilter
            label="Add admin"
            leadingIcon={<OutlinePlusCircleIcon aria-hidden />}
            options={addOptions}
            onChange={handleAdd}
            disabled={addOptions.length === 0}
          />

          {/* The DS Dialog panel has no max height and the body scroll is
              locked behind it, so a long chip list scrolls here and Save
              stays on screen. */}
          <Box className="overflow-y-auto max-h-96">
            <Row gap={2} wrap="wrap">
              {selectedIds.map((internalUserId) => (
                <Chip
                  key={internalUserId}
                  label={chipLabel(namesById, internalUserId)}
                  onDelete={() => handleRemove(internalUserId)}
                  // The DS takes the delete button out of the tab order and
                  // removes on Backspace/Delete instead, but makes only a
                  // clickable chip focusable — without this a keyboard user
                  // could not remove anyone.
                  tabIndex={0}
                />
              ))}
            </Row>
          </Box>

          {submitError ? (
            <Typography role="alert" variant="body2" className="text-error">
              {submitError}
            </Typography>
          ) : null}
        </Column>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={saveAdmins.isPending}
          disabled={isSameSet(selectedIds, metro.assignedInternalUserIds)}
          onClick={handleSave}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

EditMetroAdminsDialog.propTypes = {
  metro: salesMetroRowPropType.isRequired,
  /** Every GoTu admin, as `useSalesMetrosPage` returns them. */
  admins: PropTypes.arrayOf(
    PropTypes.shape({
      internalUserId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditMetroAdminsDialog;
